import React from 'react';
import styled from 'styled-components';
import { Space, Tag, message } from 'antd';
import { HeartOutlined, ShareAltOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const StyledWrapper = styled.div`
  .card {
    position: relative;
    width: 100%;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0px 1px 13px rgba(0,0,0,0.1);
    cursor: pointer;
    transition: all 0.3s ease;
    overflow: hidden;

    &:hover {
      transform: translateY(-6px);
      box-shadow: 0px 8px 20px rgba(0,0,0,0.12);
    }

    &:active {
      transform: scale(0.98);
    }
  }

  .image-container {
    position: relative;
    width: 100%;
    padding-top: 100%; // 1:1 宽高比
    background: #f5f5f5;
    overflow: hidden;
  }

  .product-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  .card:hover .product-image {
    transform: scale(1.05);
  }

  .content {
    padding: 12px;
  }

  .title {
    font-size: 16px;
    font-weight: 500;
    color: #333;
    margin-bottom: 8px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.4;
  }

  .tags {
    margin-bottom: 8px;
    min-height: 22px;
  }

  .price {
    font-size: 18px;
    font-weight: bold;
    color: #D4303B;
  }

  .actions {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    gap: 8px;
    opacity: 0;
    transform: translateY(-10px);
    transition: all 0.3s ease;
  }

  .card:hover .actions {
    opacity: 1;
    transform: translateY(0);
  }

  .action-button {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #fff;
      transform: scale(1.1);
    }

    .anticon {
      font-size: 16px;
      color: #333;
    }
  }

  @media (max-width: 768px) {
    .title {
      font-size: 14px;
      -webkit-line-clamp: 1;
    }

    .price {
      font-size: 16px;
    }

    .content {
      padding: 8px;
    }

    .actions {
      top: 4px;
      right: 4px;
      gap: 4px;
    }

    .action-button {
      width: 28px;
      height: 28px;

      .anticon {
        font-size: 14px;
      }
    }
  }
`;

// 添加汇率配置
const EXCHANGE_RATES = {
  CNY: 1,
  USD: 0.14, // 1 CNY = 0.14 USD
};

const formatCurrency = (amount, currency) => {
  const formatter = new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'zh-CN', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(amount);
};

const ProductCard = ({ 
  product, 
  categoryName,
  onClick,
  onShare,
  onLike 
}) => {
  const { t, i18n } = useTranslation();
  const { id, title, price, images, tags = [] } = product;
  const mainImage = images.find(img => img.type === 'main')?.url;

  // 根据语言选择货币和计算价格
  const currency = i18n.language === 'zh' ? 'CNY' : 'USD';
  const exchangeRate = EXCHANGE_RATES[currency];
  const convertedPrice = price * exchangeRate;
  const formattedPrice = formatCurrency(convertedPrice, currency);

  // 处理分享功能
  const handleShare = (e) => {
    e.stopPropagation();
    // 构建商品链接
    const productUrl = `${window.location.origin}/product/${id}`;
    
    // 使用 Clipboard API 复制链接
    navigator.clipboard.writeText(productUrl)
      .then(() => {
        message.success(t('common.share.linkCopied', {
          defaultValue: i18n.language === 'zh' ? '商品链接已复制到剪贴板' : 'Product link copied to clipboard'
        }));
        onShare?.(product);
      })
      .catch(() => {
        // 如果 Clipboard API 失败，使用传统方法
        const textarea = document.createElement('textarea');
        textarea.value = productUrl;
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          message.success(t('common.share.linkCopied', {
            defaultValue: i18n.language === 'zh' ? '商品链接已复制到剪贴板' : 'Product link copied to clipboard'
          }));
          onShare?.(product);
        } catch (err) {
          message.error(t('common.share.copyFailed', {
            defaultValue: i18n.language === 'zh' ? '复制链接失败，请手动复制' : 'Failed to copy link, please copy manually'
          }));
        }
        document.body.removeChild(textarea);
      });
  };

  return (
    <StyledWrapper>
      <div className="card" onClick={onClick}>
        <div className="image-container">
          <img 
            className="product-image" 
            src={mainImage} 
            alt={title}
          />
          <div className="actions">
            <div 
              className="action-button"
              onClick={(e) => {
                e.stopPropagation();
                onLike?.(product);
              }}
            >
              <HeartOutlined />
            </div>
            <div 
              className="action-button"
              onClick={handleShare}
            >
              <ShareAltOutlined />
            </div>
          </div>
        </div>
        <div className="content">
          <div className="title">{title}</div>
          <div className="tags">
            <Space size={[0, 4]} wrap>
              <Tag color="blue">{categoryName}</Tag>
              {tags.slice(0, 2).map((tag, index) => (
                <Tag 
                  key={index} 
                  color={index % 2 ? '#D4303B' : '#282C34'}
                >
                  {tag}
                </Tag>
              ))}
              {tags.length > 2 && (
                <Tag>+{tags.length - 2}</Tag>
              )}
            </Space>
          </div>
          <div className="price">{formattedPrice}</div>
        </div>
      </div>
    </StyledWrapper>
  );
};

export default ProductCard; 